// Layer 4 (UI): concentrate quality and losses (paul-feedback.A5 / #265),
// restored to the Senior metallurgist landing and reshaped to its locked comp
// (polish.P27 #714, `.impeccable/mocks/decision/landing-senior-met.html` A,
// adjudication 11) — renders `concentrateQualityModel()` only: the concentrate
// grades against the spec, the tails losses from the closed mass balance, and
// the penalty elements one column per concentrate, each with its headroom
// meter against the contract limit. Provenance rides each row's tooltip.
// See docs/logic/overview/concentrate-quality-and-losses.md.

import { HelpAnchor } from '@/design-system';
import { cn } from '@/shared/cn';
import { Commentable } from '@/shared/comments/Commentable';
import { GmCard } from './GmTabs';
import { SectionHead, statePill } from './SeniorMetTabs';
import {
  concentrateQualityModel,
  type ConcentrateTone,
  type PenaltyMeterRowView,
} from './ConcentrateQualityPanel.transform';

/** A grade or tails row (comp `.cq .row`): label, figure, comparison, state. */
function QualityRow({
  label,
  valueText,
  targetText,
  tone,
  statusLabel,
  title,
  dataId,
}: {
  label: string;
  valueText: string;
  targetText: string;
  tone: ConcentrateTone;
  statusLabel: string;
  title: string;
  dataId: string;
}) {
  return (
    // At 1400 px and wider the comparison sits in its own column (the comp);
    // narrower, it drops under the figure so the label never truncates.
    <li
      className="grid grid-cols-[minmax(0,1fr)_auto_64px] items-center gap-x-3 border-b border-[var(--color-divider)] py-1.5 text-[12.5px] tabular-nums last:border-b-0 min-[1400px]:grid-cols-[minmax(0,1fr)_auto_minmax(96px,auto)_64px]"
      title={title}
      data-smet-quality-row={dataId}
    >
      <span className="row-span-2 min-w-0 font-medium text-ink-primary min-[1400px]:row-span-1">{label}</span>
      <span className="whitespace-nowrap text-right font-semibold text-ink-primary">{valueText}</span>
      <span className="col-start-2 row-start-2 whitespace-nowrap text-right text-[11px] text-muted min-[1400px]:col-start-3 min-[1400px]:row-start-1 min-[1400px]:text-left min-[1400px]:text-[11.5px]">
        {targetText}
      </span>
      <span className="col-start-3 row-span-2 row-start-1 justify-self-end min-[1400px]:col-start-4 min-[1400px]:row-span-1">
        <span className={statePill(tone)} data-smet-quality-state={tone}>
          {statusLabel}
        </span>
      </span>
    </li>
  );
}

/** A penalty element (comp `.prow`): element, assay `of` limit, the 5 px
 *  headroom meter (neutral when OK, amber at WATCH) and the state pill. */
function PenaltyRow({ row }: { row: PenaltyMeterRowView }) {
  return (
    <li
      className="grid grid-cols-[20px_minmax(0,1fr)_auto] items-center gap-x-1.5 border-b border-[var(--color-divider)] py-[5px] text-[12.5px] tabular-nums last:border-b-0 min-[1400px]:grid-cols-[20px_minmax(0,1fr)_26px_auto]"
      title={row.provenance}
      data-smet-penalty-row={row.element}
    >
      <span className="font-semibold text-ink-primary">{row.element}</span>
      <span className="min-w-0 font-semibold text-ink-primary">
        <span className="whitespace-nowrap">{row.valueText}</span>{' '}
        <span className="whitespace-nowrap text-[11.5px] font-normal text-muted">of {row.limitText}</span>
      </span>
      {/* The meter needs the comp's 1470 column; narrower, the pill carries the state. */}
      <span className="relative hidden h-[5px] overflow-hidden rounded-full bg-[var(--hairline-strong)] min-[1400px]:block" aria-hidden="true">
        <span
          className={cn(
            'absolute inset-y-0 left-0 rounded-full',
            row.tone === 'warn' ? 'bg-[var(--state-warning)]' : row.tone === 'bad' ? 'bg-[var(--state-blocked)]' : 'bg-[var(--ink-tertiary)]',
          )}
          style={{ width: `${Math.round(row.meterRatio * 100)}%` }}
          data-smet-penalty-meter={Math.round(row.meterRatio * 100)}
        />
      </span>
      <span className="justify-self-end">
        <span className={statePill(row.tone)}>{row.statusLabel}</span>
      </span>
    </li>
  );
}

export function ConcentrateQualityPanel({ className }: { className?: string }) {
  const model = concentrateQualityModel();
  return (
    <Commentable name="ConcentrateQuality">
      <GmCard
        title="Concentrate quality and losses"
        meta={<HelpAnchor doc="overview/concentrate-quality-and-losses" />}
        className={cn('h-full', className)}
        data-smet-concentrate
      >
        <SectionHead>Concentrate grade</SectionHead>
        <ul>
          {model.gradeRows.map((row) => (
            <QualityRow
              key={row.id}
              dataId={row.id}
              label={row.label}
              valueText={row.valueText}
              targetText={row.targetText}
              tone={row.tone}
              statusLabel={row.statusLabel}
              title={[row.readingProvenance, row.targetProvenance].filter(Boolean).join(' · ')}
            />
          ))}
        </ul>
        <SectionHead className="mt-2.5">Tails losses (today)</SectionHead>
        <ul>
          {model.tailsRows.map((row) => (
            <QualityRow
              key={row.element}
              dataId={`tails-${row.element}`}
              label={`${row.element} tails`}
              valueText={row.assayText}
              targetText={row.targetText}
              tone={row.tone}
              statusLabel={row.statusLabel}
              title={[row.provenance, row.targetProvenance].filter(Boolean).join(' · ')}
            />
          ))}
        </ul>
        <SectionHead className="mt-2.5" meta="value of contract limit">
          Penalty elements
        </SectionHead>
        <div className="grid grid-cols-1 gap-x-[18px] sm:grid-cols-2">
          {model.penaltyMeterGroups.map((group) => (
            <div key={group.id} className="min-w-0" data-smet-penalty-group={group.id}>
              <p className="border-b border-[var(--color-divider)] pb-[3px] pt-1 text-[11.5px] font-semibold text-ink-secondary">
                {group.label}
              </p>
              <ul>
                {group.rows.map((row) => (
                  <PenaltyRow key={`${group.id}-${row.element}`} row={row} />
                ))}
              </ul>
            </div>
          ))}
        </div>
      </GmCard>
    </Commentable>
  );
}
